export type LearningEventName =
  | "track_view"
  | "course_card_view"
  | "course_card_click"
  | "course_start"
  | "course_resume"
  | "lesson_start"
  | "lesson_complete"
  | "chapter_complete"
  | "course_complete"
  | "paywall_view"
  | "upgrade_click"
  | "roadmap_click";

export type LearningUserStatus = "logged_in" | "logged_out";
export type LearningAccessType = "free" | "paid";
export type LearningDeviceType = "mobile" | "tablet" | "desktop";

export interface LearningEventProperties {
  track_slug?: string;
  course_slug?: string;
  lesson_id?: string;
  roadmap_slug?: string;
  source_page?: string;
  user_status?: LearningUserStatus;
  access_type?: LearningAccessType;
  progress_rate?: number;
  device_type?: LearningDeviceType;
  [property: string]: string | number | boolean | undefined;
}

export type LearningTrackEvent = (
  event: string,
  properties?: Record<string, unknown>,
) => void;

/**
 * This is a viewport classification, not a claim about the user's hardware.
 * The labels intentionally match the existing analytics `device_type` field.
 */
export function deviceTypeForViewport(
  viewportWidth: number,
): LearningDeviceType | undefined {
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) return undefined;
  if (viewportWidth < 768) return "mobile";
  if (viewportWidth < 1024) return "tablet";
  return "desktop";
}

export function currentLearningDeviceType(): LearningDeviceType | undefined {
  if (typeof window === "undefined") return undefined;
  return deviceTypeForViewport(window.innerWidth);
}

export function learningUserStatus(
  user: unknown,
  isAuthLoading: boolean,
): LearningUserStatus | undefined {
  if (isAuthLoading) return undefined;
  return user ? "logged_in" : "logged_out";
}

export function learningAccessType(isFree: boolean): LearningAccessType {
  return isFree ? "free" : "paid";
}

export function learningProgressRate(
  completed: number,
  total: number,
): number | undefined {
  if (
    !Number.isFinite(completed) ||
    !Number.isFinite(total) ||
    completed < 0 ||
    total <= 0
  ) {
    return undefined;
  }

  return Math.min(100, Math.max(0, Math.round((completed / total) * 100)));
}

export function compactLearningEventProperties(
  properties: LearningEventProperties,
): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(properties).filter((entry) => entry[1] !== undefined),
  ) as Record<string, string | number | boolean>;
}

/**
 * Analytics is observational. A capture failure must never block rendering or
 * the navigation that the learner explicitly requested.
 */
export function captureLearningEvent(
  trackEvent: LearningTrackEvent,
  event: LearningEventName,
  properties: LearningEventProperties,
): void {
  try {
    trackEvent(
      event,
      compactLearningEventProperties({
        ...properties,
        device_type: properties.device_type ?? currentLearningDeviceType(),
      }),
    );
  } catch {
    // Fail open: navigation and lesson rendering are more important than telemetry.
  }
}
